import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import Home from 'svg/Home';
import Net from 'svg/Net';
import Outdoor from 'svg/Outdoor';
import Surface from 'svg/Surface';
import classNames from 'styles/utils/classNames';
import { BookableCourt } from './types';
import { getIsDiscounted } from './utils';

interface Props {
  court: BookableCourt;
  isSelected: boolean;
  onSelect: (courtId: string) => void;
}

const iconClasses =
  'h-3.5 w-3.5 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon';

export default function CourtOption({ court, isSelected, onSelect }: Props) {
  const price = convertUnitPriceToFormattedPrice(court.priceUnitAmount, court.currency);
  const isDiscounted = getIsDiscounted(court);
  const listPrice = isDiscounted
    ? convertUnitPriceToFormattedPrice(court.listPriceUnitAmount as number, court.currency)
    : null;

  const attributes = [
    {
      key: 'placement',
      Icon: court.isIndoor ? Home : Outdoor,
      label: court.isIndoor ? 'Indoor' : 'Outdoor',
    },
    court.netType && {
      key: 'net',
      Icon: Net,
      label: court.netType === 'PERMANENT' ? 'Permanent' : 'Portable',
    },
    court.surfaceLabel && { key: 'surface', Icon: Surface, label: court.surfaceLabel },
  ].filter(Boolean) as { key: string; Icon: typeof Home; label: string }[];

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={() => onSelect(court.id)}
      className={classNames(
        'flex w-full flex-col gap-ds-md rounded-xl border bg-color-bg-lightmode-primary p-ds-md text-left transition-shadow dark:bg-color-bg-darkmode-primary',
        isSelected
          ? 'border-color-border-hover-lightmode shadow-lightmode-primary dark:border-color-border-hover-darkmode'
          : 'border-color-border-card-lightmode dark:border-color-border-card-darkmode',
      )}
    >
      <div className="flex w-full items-center justify-between gap-ds-sm">
        <span className="flex items-center gap-ds-xs">
          <img
            src="/images/app/court-colored.svg"
            alt=""
            aria-hidden
            className="h-6 w-6 shrink-0"
          />
          <span className="typography-product-subheading-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {court.name}
          </span>
        </span>
        <span className="flex shrink-0 items-center">
          {listPrice && (
            <span className="typography-product-caption mr-1 text-color-text-lightmode-tertiary line-through dark:text-color-text-darkmode-tertiary">
              {listPrice.priceDisplay}
            </span>
          )}
          <span className="typography-product-subheading-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {price.priceDisplay}
          </span>
          <span className="typography-product-caption text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
            /hour
          </span>
        </span>
      </div>

      <span className="h-px w-full bg-color-border-card-lightmode dark:bg-color-border-card-darkmode" />

      <span className="flex flex-wrap items-center gap-x-ds-md gap-y-ds-xs">
        {attributes.map(({ key, Icon, label }) => (
          <span key={key} className="flex items-center gap-ds-xs">
            <Icon className={iconClasses} />
            <span className="typography-product-text-card-mobile text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {label}
            </span>
          </span>
        ))}
      </span>
    </button>
  );
}
